import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from '@jq4race/i18n';
import { type CreateEventGuestRequest, CreateEventGuestRequestSchema } from '@jq4race/shared';
import { Button, Input, Label } from '@jq4race/ui';
import { TriangleAlert } from 'lucide-react';
import { forwardRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { translateApiError } from '../../../../shared/errors/translateApiError';
import { GUEST_BILLING_MODE_OPTIONS } from '../constants';

interface AddGuestFormProps {
  onSubmit: (values: CreateEventGuestRequest) => Promise<void>;
  disabled?: boolean;
}

export function AddGuestForm({ onSubmit, disabled }: AddGuestFormProps) {
  const { t } = useTranslation('events');
  const [serverError, setServerError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<CreateEventGuestRequest>({
    resolver: zodResolver(CreateEventGuestRequestSchema),
    defaultValues: { name: '', plate: null, billingMode: undefined },
  });

  async function handleFormSubmit(values: CreateEventGuestRequest) {
    setServerError(null);
    try {
      await onSubmit(values);
      reset({ name: '', plate: null, billingMode: undefined });
    } catch (e) {
      setServerError(translateApiError(e, t, { fallback: t('addGuestForm.genericError') }));
    }
  }

  const isDisabled = disabled || isSubmitting;

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-3">
      {serverError && <FormBanner message={serverError} />}

      <div className="grid gap-4 sm:grid-cols-[2fr_1fr_1fr_auto] sm:items-end">
        <Field
          label={t('addGuestForm.name.label')}
          htmlFor="guest-name"
          error={errors.name?.message}
        >
          <Input
            id="guest-name"
            placeholder={t('addGuestForm.name.placeholder')}
            disabled={isDisabled}
            {...register('name')}
          />
        </Field>

        <Field
          label={t('addGuestForm.plate.label')}
          htmlFor="guest-plate"
          error={errors.plate?.message}
        >
          <Input
            id="guest-plate"
            placeholder={t('addGuestForm.plate.placeholder')}
            disabled={isDisabled}
            {...register('plate', {
              setValueAs: (v: string) => (v === '' ? null : v),
            })}
          />
        </Field>

        <Field
          label={t('addGuestForm.billing')}
          htmlFor="guest-billing"
          error={errors.billingMode?.message}
        >
          <SelectInput
            id="guest-billing"
            disabled={isDisabled}
            {...register('billingMode', {
              setValueAs: (v: string) => (v === '' ? undefined : v),
            })}
          >
            <option value="">{t('constants.unset')}</option>
            {GUEST_BILLING_MODE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {t(`constants.guestBillingMode.${o.value}`)}
              </option>
            ))}
          </SelectInput>
        </Field>

        <Button type="submit" size="sm" disabled={isDisabled}>
          {isSubmitting ? t('addGuestForm.adding') : t('addGuestForm.addGuest')}
        </Button>
      </div>
    </form>
  );
}

function Field({
  label,
  htmlFor,
  error,
  children,
}: {
  label: string;
  htmlFor: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <Label htmlFor={htmlFor} className="mb-1.5 block text-[13px] font-medium text-jq-charcoal">
        {label}
      </Label>
      {children}
      {error && (
        <p role="alert" className="mt-1.5 text-[12px] text-jq-danger">
          {error}
        </p>
      )}
    </div>
  );
}

const SelectInput = forwardRef<HTMLSelectElement, React.SelectHTMLAttributes<HTMLSelectElement>>(
  function SelectInput({ className, ...rest }, ref) {
    return (
      <select
        ref={ref}
        {...rest}
        className={`flex h-10 w-full rounded-sm border border-jq-sand bg-jq-surface-0 px-3 text-[14px] text-jq-charcoal shadow-sm transition focus:border-jq-charcoal focus:outline-none focus:ring-1 focus:ring-jq-charcoal disabled:cursor-not-allowed disabled:opacity-50 ${
          className ?? ''
        }`}
      />
    );
  },
);

function FormBanner({ message }: { message: string }) {
  return (
    <div
      role="alert"
      className="flex items-start gap-3 rounded-sm border border-jq-danger/40 bg-jq-danger/10 px-4 py-3 text-[13px] text-jq-danger"
    >
      <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.75} />
      <p className="flex-1 leading-snug">{message}</p>
    </div>
  );
}
